// Spanish landing pages (option 1: targeted /es pages, not a full mirror).
// Every entry pairs a Spanish URL with its English counterpart so both sides
// can emit matching hreflang alternates from one source of truth.

import { schemaDateFields } from "@/lib/schema-dates";

export const SITE = "https://virginiabariatric.com";

export const OG_IMAGE =
  "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg";

export interface EsPage {
  /** Spanish route path, e.g. "/es/manga-gastrica" */
  es: string;
  /** English counterpart path, e.g. "/services/sleeve" */
  en: string;
  /** Short Spanish label used in the /es section nav */
  label: string;
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImageAlt: string;
  /** Card slug under public/og/es (og-<card>.png + sized variants). */
  card: string;
  /**
   * Spanish-only page with no English twin: emit a self-referencing es
   * alternate instead of claiming an English counterpart already owned by
   * another /es page.
   */
  noAlternate?: boolean;
  /**
   * Deep pages that live under a hub rather than in the top-level Spanish
   * section nav. They are still sitemapped and linked from their hub page.
   */
  inNav?: boolean;
}


export const ES_PAGES = {
  services: {
    es: "/es/servicios",
    en: "/services",
    label: "Servicios",
    title: "Cirugía Bariátrica en el Norte de Virginia | Virginia Bariatric",
    description: "Compare la manga gástrica, el bypass, la banda, la ESG, el balón y la cirugía de revisión con el Dr. Matthew Fitzer en el norte de Virginia.",
    ogTitle: "Cirugía Bariátrica en el Norte de Virginia | Virginia Bariatric",
    ogDescription: "Compare la manga gástrica, el bypass, la banda, la ESG, el balón y la cirugía de revisión con el Dr. Matthew Fitzer en el norte de Virginia.",
    ogImageAlt: "Opciones de cirugía bariátrica en Virginia Bariatric Surgery",
    card: "servicios",
  },
  weightRegainGuide: {
    es: "/es/recuperacion-de-peso-despues-de-cirugia-bariatrica",
    en: "/resources/managing-weight-regain-after-bariatric-surgery",
    label: "Recuperación de peso después de cirugía",
    title: "Recuperación de Peso Después de Cirugía Bariátrica | Virginia Bariatric",
    description: "Por qué se recupera peso después de la manga o el bypass y cómo se evalúa: medicamentos GLP-1, cirugía de revisión y seguimiento a largo plazo.",
    ogTitle: "Recuperación de Peso Después de Cirugía Bariátrica | Virginia Bariatric",
    ogDescription: "Por qué se recupera peso después de la manga o el bypass y cómo se evalúa: medicamentos GLP-1, cirugía de revisión y seguimiento a largo plazo.",
    ogImageAlt: "Guía para la recuperación de peso después de cirugía bariátrica",
    card: "recuperacion-de-peso-cirugia",
    inNav: false,
  },
  home: {
    es: "/es",
    en: "/",
    label: "Inicio",
    title: "Cirugía Bariátrica en Virginia | Virginia Bariatric",
    description:
      "Cirugía bariátrica y pérdida de peso con medicamentos en el norte de Virginia. El Dr. Matthew Fitzer, cirujano certificado, suma más de 6,000 operaciones.",
    ogTitle: "Cirugía Bariátrica en Virginia | Virginia Bariatric",
    ogDescription: "Cirugía bariátrica y pérdida de peso con medicamentos en el norte de Virginia. El Dr. Matthew Fitzer, cirujano certificado, suma más de 6,000 operaciones.",
    ogImageAlt:
      "Virginia Bariatric Surgery — práctica de cirugía bariátrica del Dr. Matthew Fitzer en el norte de Virginia",
    card: "home",
  },
  sleeve: {
    es: "/es/manga-gastrica",
    en: "/services/sleeve",
    label: "Manga gástrica",
    title: "Gastrectomía en Manga en el Norte de Virginia | Virginia Bariatric",
    description:
      "Qué es la manga gástrica, cómo se realiza, requisitos, riesgos y resultados. Cirugía laparoscópica en Herndon, Virginia, con el Dr. Matthew Fitzer.",
    ogTitle: "Gastrectomía en Manga en el Norte de Virginia | Virginia Bariatric",
    ogDescription: "Qué es la manga gástrica, cómo se realiza, requisitos, riesgos y resultados. Cirugía laparoscópica en Herndon, Virginia, con el Dr. Matthew Fitzer.",
    ogImageAlt: "Cirugía de manga gástrica en Virginia Bariatric Surgery",
    card: "manga-gastrica",
  },
  hiatalHernia: {
    es: "/es/reparacion-de-hernia-hiatal",
    en: "/services/hiatal-hernia-surgery",
    label: "Reparación de hernia hiatal",
    title: "Cirugía de Hernia Hiatal en el Norte de Virginia | Virginia Bariatric",
    description:
      "Reparación de hernia hiatal en el norte de Virginia con el Dr. Matthew Fitzer: laparoscopía, funduplicatura, reoperación y reflujo tras manga.",
    ogTitle: "Cirugía de Hernia Hiatal en el Norte de Virginia | Virginia Bariatric",
    ogDescription:
      "Reparación de hernia hiatal en el norte de Virginia con el Dr. Matthew Fitzer: laparoscopía, funduplicatura, reoperación y reflujo tras manga.",
    ogImageAlt: "Cirugía y reparación de hernia hiatal en Virginia Bariatric Surgery",
    card: "hernia-hiatal",
  },
  bypass: {
    es: "/es/bypass-gastrico",
    en: "/services/bypass",
    label: "Bypass gástrico",
    title: "Cirugía de Bypass Gástrico en el Norte de Virginia | Virginia Bariatric",
    description:
      "El bypass gástrico Roux-en-Y explicado en español: cómo funciona, a quién beneficia, recuperación, riesgos y pérdida de peso esperada en Herndon, Virginia.",
    ogTitle: "Cirugía de Bypass Gástrico en el Norte de Virginia | Virginia Bariatric",
    ogDescription: "El bypass gástrico Roux-en-Y explicado en español: cómo funciona, a quién beneficia, recuperación, riesgos y pérdida de peso esperada en Herndon, Virginia.",
    ogImageAlt: "Cirugía de bypass gástrico en Virginia Bariatric Surgery",
    card: "bypass-gastrico",
  },
  balloon: {
    es: "/es/balon-gastrico",
    en: "/services/balloon",
    label: "Balón gástrico",
    title: "Balón Gástrico (Orbera®) en el Norte de Virginia | Virginia Bariatric",
    description:
      "El balón gástrico es un procedimiento sin cirugía para bajar de peso: se coloca por endoscopia y dura seis meses. Precios y resultados en Virginia.",
    ogTitle: "Balón Gástrico (Orbera®) en el Norte de Virginia | Virginia Bariatric",
    ogDescription: "El balón gástrico es un procedimiento sin cirugía para bajar de peso: se coloca por endoscopia y dura seis meses. Precios y resultados en Virginia.",
    ogImageAlt: "Balón gástrico Orbera® en Virginia Bariatric Surgery",
    card: "balon-gastrico",
  },
  esg: {
    es: "/es/gastroplastia-endoscopica-en-manga",
    en: "/services/esg",
    label: "Manga endoscópica (ESG)",
    title: "Procedimiento de Pérdida de Peso ESG en Virginia | Virginia Bariatric",
    description:
      "La ESG es un procedimiento ambulatorio sin incisiones que remodela el estómago con suturas internas, a cargo del Dr. Fitzer en el área de Washington, D.C.",
    ogTitle: "Procedimiento de Pérdida de Peso ESG en Virginia | Virginia Bariatric",
    ogDescription:
      "La ESG es un procedimiento ambulatorio sin incisiones que remodela el estómago con suturas internas, a cargo del Dr. Fitzer en el área de Washington, D.C.",
    ogImageAlt: "Gastroplastia endoscópica en manga (ESG) en Virginia Bariatric Surgery",
    card: "esg",
    inNav: false,
  },
  medical: {
    es: "/es/perdida-de-peso-con-medicamentos",
    en: "/medical-weight-loss",
    label: "Medicamentos",
    title: "Pérdida de Peso con Medicamentos GLP-1 | Virginia Bariatric",
    description:
      "Programa médico de pérdida de peso con Wegovy, Zepbound, Ozempic y Mounjaro, supervisado por un médico en el norte de Virginia. Información en español.",
    ogTitle: "Pérdida de Peso con Medicamentos GLP-1 | Virginia Bariatric",
    ogDescription: "Programa médico de pérdida de peso con Wegovy, Zepbound, Ozempic y Mounjaro, supervisado por un médico en el norte de Virginia. Información en español.",
    ogImageAlt: "Programa médico de pérdida de peso en Virginia Bariatric Surgery",
    card: "medicamentos",
  },
  insurance: {
    es: "/es/cobertura-de-seguro",
    en: "/medical-weight-loss/insurance",
    label: "Seguro médico",
    title: "Cobertura de Seguro para Cirugía y Medicamentos | Virginia Bariatric",
    description:
      "Cobertura de seguro para la cirugía bariátrica y los medicamentos GLP-1 en Virginia: requisitos, documentación, negaciones y pago propio.",
    ogTitle: "Cobertura de Seguro para Cirugía y Medicamentos | Virginia Bariatric",
    ogDescription: "Cobertura de seguro para la cirugía bariátrica y los medicamentos GLP-1 en Virginia: requisitos, documentación, negaciones y pago propio.",
    ogImageAlt: "Información de cobertura de seguro de Virginia Bariatric Surgery",
    card: "seguro",
  },
  local: {
    es: "/es/cirugia-bariatrica-virginia",
    en: "/contact",
    label: "Herndon, VA",
    title: "Cirugía Bariátrica en Herndon, Virginia | Virginia Bariatric",
    description:
      "Consultorio de cirugía bariátrica en Herndon, Virginia: dirección, horario, teléfono y formulario en español para pedir cita con el Dr. Fitzer.",
    ogTitle: "Cirugía Bariátrica en Herndon, Virginia | Virginia Bariatric",
    ogDescription: "Consultorio de cirugía bariátrica en Herndon, Virginia: dirección, horario, teléfono y formulario en español para pedir cita con el Dr. Fitzer.",
    ogImageAlt:
      "Consultorio de cirugía bariátrica del Dr. Matthew Fitzer en Herndon, Virginia",
    card: "cirugia-bariatrica-virginia",
    noAlternate: true,
  },
  about: {
    es: "/es/sobre-el-doctor",
    en: "/about",
    label: "Sobre el doctor",
    title: "Matthew Fitzer, M.D., Cirujano Bariátrico | Virginia Bariatric",
    description:
      "Conozca al Dr. Matthew Fitzer, cirujano bariátrico certificado en el norte de Virginia. Más de 6,000 operaciones laparoscópicas desde 2005.",
    ogTitle: "Matthew Fitzer, M.D., Cirujano Bariátrico | Virginia Bariatric",
    ogDescription: "Conozca al Dr. Matthew Fitzer, cirujano bariátrico certificado en el norte de Virginia. Más de 6,000 operaciones laparoscópicas desde 2005.",
    ogImageAlt: "Dr. Matthew Fitzer, cirujano bariátrico certificado en Virginia Bariatric Surgery",
    card: "sobre-el-doctor",
  },
  contact: {
    es: "/es/contacto",
    en: "/contact",
    label: "Contacto",
    title: "Contáctenos en Español | Virginia Bariatric",
    description:
      "Llame al (703) 709-9771 o envíe un mensaje al consultorio del Dr. Fitzer en Herndon, Virginia. Atendemos consultas en español.",
    ogTitle: "Contáctenos en Español | Virginia Bariatric",
    ogDescription: "Llame al (703) 709-9771 o envíe un mensaje al consultorio del Dr. Fitzer en Herndon, Virginia. Atendemos consultas en español.",
    ogImageAlt: "Consultorio de Virginia Bariatric Surgery en Herndon, Virginia",
    card: "contacto",
  },
  stories: {
    es: "/es/antes-y-despues",
    en: "/results",
    label: "Antes y después",
    title: "Fotos de Antes y Después | Virginia Bariatric",
    description:
      "Fotos reales de antes y después de pacientes de cirugía bariátrica del Dr. Matthew Fitzer en el norte de Virginia: resultados de manga y bypass gástrico.",
    ogTitle: "Fotos de Antes y Después | Virginia Bariatric",
    ogDescription: "Fotos reales de antes y después de pacientes de cirugía bariátrica del Dr. Matthew Fitzer en el norte de Virginia: resultados de manga y bypass gástrico.",
    ogImageAlt:
      "Fotos de antes y después de pacientes de cirugía de pérdida de peso en Virginia Bariatric Surgery",
    card: "antes-y-despues",
  },
  medications: {
    es: "/es/medicamentos-para-bajar-de-peso",
    en: "/medical-weight-loss/medications",
    label: "Guía de medicamentos",
    title: "Medicamentos GLP-1 para bajar de peso en Virginia | Virginia Bariatric",
    description:
      "Semaglutida, tirzepatida y pastillas para bajar de peso explicadas por el Dr. Fitzer: quién califica, qué esperar, seguridad y cobertura en Virginia.",
    ogTitle: "Medicamentos GLP-1 para bajar de peso en Virginia | Virginia Bariatric",
    ogDescription: "Semaglutida, tirzepatida y pastillas para bajar de peso explicadas por el Dr. Fitzer: quién califica, qué esperar, seguridad y cobertura en Virginia.",
    ogImageAlt:
      "Guía de medicamentos para bajar de peso de Virginia Bariatric Surgery",
    card: "medicamentos-guia",
    inNav: false,
  },
  semaglutide: {
    es: "/es/semaglutida",
    en: "/medical-weight-loss/semaglutide",
    label: "Semaglutida",
    title: "Semaglutida (Wegovy®) en el Norte de Virginia | Virginia Bariatric",
    description:
      "Semaglutida (Wegovy®, Ozempic®) para bajar de peso en Herndon, Virginia, recetada y vigilada por un cirujano bariátrico certificado. Inyección o pastilla.",
    ogTitle: "Semaglutida (Wegovy®) en el Norte de Virginia | Virginia Bariatric",
    ogDescription: "Semaglutida (Wegovy®, Ozempic®) para bajar de peso en Herndon, Virginia, recetada y vigilada por un cirujano bariátrico certificado. Inyección o pastilla.",
    ogImageAlt:
      "Semaglutida (Wegovy® y Ozempic®) para bajar de peso en Virginia Bariatric Surgery",
    card: "semaglutida",
    inNav: false,
  },
  tirzepatide: {
    es: "/es/tirzepatida",
    en: "/medical-weight-loss/tirzepatide",
    label: "Tirzepatida",
    title: "Tirzepatida (Zepbound®) en el Norte de Virginia | Virginia Bariatric",
    description:
      "Tirzepatida (Zepbound®, Mounjaro®) recetada por un cirujano bariátrico certificado en Herndon, Virginia: el medicamento para bajar de peso más eficaz.",
    ogTitle: "Tirzepatida (Zepbound®) en el Norte de Virginia | Virginia Bariatric",
    ogDescription: "Tirzepatida (Zepbound®, Mounjaro®) recetada por un cirujano bariátrico certificado en Herndon, Virginia: el medicamento para bajar de peso más eficaz.",
    ogImageAlt:
      "Tirzepatida (Zepbound® y Mounjaro®) para bajar de peso en Virginia Bariatric Surgery",
    card: "tirzepatida",
    inNav: false,
  },
  oralMedications: {
    es: "/es/medicamentos-orales-para-bajar-de-peso",
    en: "/medical-weight-loss/oral-medications",
    label: "Medicamentos orales",
    title: "Fentermina y Contrave® en el Norte de Virginia | Virginia Bariatric",
    description:
      "Fentermina, Contrave®, orlistat y dietilpropión recetados por el Dr. Fitzer en Herndon, Virginia. Opciones orales económicas que no son GLP-1.",
    ogTitle: "Medicamentos Orales para Bajar de Peso | Virginia Bariatric",
    ogDescription:
      "Fentermina, Contrave®, orlistat y dietilpropión: opciones orales económicas recetadas y supervisadas por el Dr. Matthew Fitzer.",
    ogImageAlt:
      "Medicamentos orales para bajar de peso recetados en Virginia Bariatric Surgery",
    card: "medicamentos-orales",
    inNav: false,
  },
  medQualifications: {
    es: "/es/quien-califica-para-medicamentos",
    en: "/medical-weight-loss/qualifications",
    label: "¿Quién califica?",
    title: "¿Califico para Medicamentos para Bajar de Peso? | Virginia Bariatric",
    description:
      "Requisitos de IMC, condiciones de salud y criterios del seguro para recetar Wegovy, Zepbound y otros medicamentos GLP-1 en el norte de Virginia.",
    ogTitle: "¿Califico para Medicamentos para Bajar de Peso? | Virginia Bariatric",
    ogDescription: "Requisitos de IMC, condiciones de salud y criterios del seguro para recetar Wegovy, Zepbound y otros medicamentos GLP-1 en el norte de Virginia.",
    ogImageAlt:
      "Requisitos para medicamentos para bajar de peso en Virginia Bariatric Surgery",
    card: "quien-califica",
    inNav: false,
  },
  insuranceCommercial: {
    es: "/es/seguro/plan-comercial-y-marketplace",
    en: "/medical-weight-loss/insurance/commercial-marketplace",
    label: "Plan comercial",
    title: "Seguro Comercial y del Marketplace para GLP-1 | Virginia Bariatric",
    description:
      "Cómo cubren Wegovy y Zepbound los planes comerciales de Virginia (Anthem, Aetna, Cigna, UnitedHealthcare) y del Marketplace. Autorización previa.",
    ogTitle: "Seguro Comercial y del Marketplace para GLP-1 | Virginia Bariatric",
    ogDescription: "Cómo cubren Wegovy y Zepbound los planes comerciales de Virginia (Anthem, Aetna, Cigna, UnitedHealthcare) y del Marketplace. Autorización previa.",
    ogImageAlt:
      "Cobertura de seguro comercial para medicamentos GLP-1 en Virginia",
    card: "seguro-comercial",
    inNav: false,
  },
  insuranceMedicaid: {
    es: "/es/seguro/medicaid-y-cardinal-care",
    en: "/medical-weight-loss/insurance/medicaid-cova",
    label: "Medicaid",
    title: "Medicaid y Cardinal Care en Virginia | Virginia Bariatric",
    description:
      "Qué cubre Medicaid de Virginia (Cardinal Care) y el plan estatal COVA para la cirugía bariátrica y los GLP-1, con los requisitos y los pasos a seguir.",
    ogTitle: "Medicaid y Cardinal Care en Virginia | Virginia Bariatric",
    ogDescription: "Qué cubre Medicaid de Virginia (Cardinal Care) y el plan estatal COVA para la cirugía bariátrica y los GLP-1, con los requisitos y los pasos a seguir.",
    ogImageAlt:
      "Cobertura de Medicaid de Virginia para cirugía bariátrica y medicamentos",
    card: "seguro-medicaid",
    inNav: false,
  },
  insuranceMedicare: {
    es: "/es/seguro/medicare-tricare-y-fehb",
    en: "/medical-weight-loss/insurance/medicare-tricare-fehb",
    label: "Medicare",
    title: "Medicare, TRICARE y FEHB en Virginia | Virginia Bariatric",
    description:
      "Cómo cubren Medicare, TRICARE y los planes federales FEHB la cirugía bariátrica y los medicamentos para bajar de peso, y qué se necesita para calificar.",
    ogTitle: "Medicare, TRICARE y FEHB en Virginia | Virginia Bariatric",
    ogDescription: "Cómo cubren Medicare, TRICARE y los planes federales FEHB la cirugía bariátrica y los medicamentos para bajar de peso, y qué se necesita para calificar.",
    ogImageAlt:
      "Cobertura de Medicare, TRICARE y FEHB para cirugía bariátrica en Virginia",
    card: "seguro-medicare",
    inNav: false,
  },
  insuranceCashPay: {
    es: "/es/seguro/pago-propio-y-negaciones",
    en: "/medical-weight-loss/insurance/cash-pay-denials",
    label: "Pago propio",
    title: "Pago Propio y Negaciones del Seguro | Virginia Bariatric",
    description:
      "Precios de pago propio para cirugía bariátrica y medicamentos GLP-1 en Virginia, más cómo apelar cuando el seguro le niega la cobertura.",
    ogTitle: "Pago Propio y Negaciones del Seguro | Virginia Bariatric",
    ogDescription: "Precios de pago propio para cirugía bariátrica y medicamentos GLP-1 en Virginia, más cómo apelar cuando el seguro le niega la cobertura.",
    ogImageAlt:
      "Opciones de pago propio y apelaciones de seguro en Virginia Bariatric Surgery",
    card: "seguro-pago-propio",
    inNav: false,
  },
  insuranceNc: {
    es: "/es/seguro/carolina-del-norte",
    en: "/medical-weight-loss/insurance/nc",
    label: "Carolina del Norte",
    title: "Cobertura de Wegovy y Zepbound en NC | Virginia Bariatric",
    description:
      "Cobertura de Wegovy y Zepbound en Carolina del Norte: el State Health Plan, Medicaid de NC, planes comerciales y opciones de pago propio.",
    ogTitle: "Cobertura de Wegovy y Zepbound en NC | Virginia Bariatric",
    ogDescription: "Cobertura de Wegovy y Zepbound en Carolina del Norte: el State Health Plan, Medicaid de NC, planes comerciales y opciones de pago propio.",
    ogImageAlt:
      "Cobertura de medicamentos para bajar de peso en Carolina del Norte",
    card: "seguro-carolina-del-norte",
    inNav: false,
  },
  surgeryInsurance: {
    es: "/es/seguro-para-cirugia-bariatrica",
    en: "/resources/insurance-coverage",
    label: "Seguro para cirugía",
    title: "Seguro para Cirugía Bariátrica en Virginia | Virginia Bariatric",
    description:
      "Guía plan por plan: Anthem, Aetna, Cigna, UnitedHealthcare, Medicare y Medicaid de Virginia. Requisitos de IMC, dieta supervisada y autorización previa.",
    ogTitle: "Seguro para Cirugía Bariátrica en Virginia | Virginia Bariatric",
    ogDescription:
      "Guía plan por plan: Anthem, Aetna, Cigna, UnitedHealthcare, Medicare y Medicaid de Virginia. Requisitos de IMC, dieta supervisada y autorización previa.",
    ogImageAlt:
      "Guía de cobertura de seguro para cirugía bariátrica en Virginia Bariatric Surgery",
    card: "seguro-cirugia",
    inNav: false,
  },
  financing: {
    es: "/es/precios-y-financiamiento",
    en: "/resources/prosper",
    label: "Precios y financiamiento",
    title: "Precios y Financiamiento con Prosper® | Virginia Bariatric",
    description:
      "Pago propio y financiamiento médico con Prosper® Healthcare Lending para cirugía bariátrica en Virginia. Consultar su tasa no afecta su crédito.",
    ogTitle: "Precios y Financiamiento con Prosper® | Virginia Bariatric",
    ogDescription:
      "Pago propio y financiamiento médico con Prosper® Healthcare Lending para cirugía bariátrica en Virginia. Consultar su tasa no afecta su crédito.",
    ogImageAlt:
      "Financiamiento para cirugía bariátrica en Virginia Bariatric Surgery",
    card: "precios-y-financiamiento",
    inNav: false,
  },

  band: {
    es: "/es/banda-gastrica",
    en: "/services/band",
    label: "Banda gástrica",
    title: "Cirugía LAP-BAND® en el Norte de Virginia | Virginia Bariatric",
    description:
      "La banda gástrica ajustable ya casi no se coloca. Si su banda da problemas, el Dr. Fitzer la retira o la convierte en manga o bypass en Herndon, Virginia.",
    ogTitle: "Cirugía LAP-BAND® en el Norte de Virginia | Virginia Bariatric",
    ogDescription: "La banda gástrica ajustable ya casi no se coloca. Si su banda da problemas, el Dr. Fitzer la retira o la convierte en manga o bypass en Herndon, Virginia.",
    ogImageAlt: "Banda gástrica y cirugía de revisión en Virginia Bariatric Surgery",
    card: "banda-gastrica",
    inNav: false,
  },
  revisions: {
    es: "/es/cirugia-de-revision",
    en: "/services/revisions",
    label: "Revisión",
    title: "Cirugía de Revisión Bariátrica en Virginia | Virginia Bariatric",
    description:
      "Revisión de manga, bypass y banda por recuperación de peso o reflujo. Re-manga y conversión a bypass en Herndon, Virginia, con el Dr. Matthew Fitzer.",
    ogTitle: "Cirugía de Revisión Bariátrica en Virginia | Virginia Bariatric",
    ogDescription: "Revisión de manga, bypass y banda por recuperación de peso o reflujo. Re-manga y conversión a bypass en Herndon, Virginia, con el Dr. Matthew Fitzer.",
    ogImageAlt: "Cirugía de revisión bariátrica en Virginia Bariatric Surgery",
    card: "cirugia-de-revision",
    inNav: false,
  },
  revisionsSleeve: {
    es: "/es/cirugia-de-revision/manga-gastrica",
    en: "/services/revisions/sleeve",
    label: "Revisión de manga",
    title: "Cirugía de Revisión de Manga Gástrica en Virginia | Virginia Bariatric",
    description:
      "Revisión de manga gástrica en Virginia por recuperación de peso o reflujo: paso a bypass, re-manga, SADI y switch duodenal. Costos, seguro y qué esperar.",
    ogTitle: "Cirugía de Revisión de Manga Gástrica en Virginia | Virginia Bariatric",
    ogDescription:
      "Revisión de manga gástrica en Virginia por recuperación de peso o reflujo: paso a bypass, re-manga, SADI y switch duodenal. Costos, seguro y qué esperar.",
    ogImageAlt: "Comparación laparoscópica de una manga gástrica normal y una manga muy dilatada, ambas delineadas en verde",
    card: "revision-manga-gastrica",
    inNav: false,
  },
  revisionsBypass: {
    es: "/es/cirugia-de-revision/bypass-gastrico",
    en: "/services/revisions/gastric-bypass",
    label: "Revisión de bypass",
    title: "Revisión del Bypass Gástrico y TORe en Virginia | Virginia Bariatric",
    description:
      "Revisión del bypass gástrico por recuperación de peso: TORe endoscópico, revisión de la bolsa y distalización en Herndon, VA, con el Dr. Matthew Fitzer.",
    ogTitle: "Revisión del Bypass Gástrico y TORe en Virginia | Virginia Bariatric",
    ogDescription:
      "Revisión del bypass gástrico por recuperación de peso: TORe endoscópico, revisión de la bolsa y distalización en Herndon, VA, con el Dr. Matthew Fitzer.",
    ogImageAlt: "Cirugía de revisión del bypass gástrico en Virginia Bariatric Surgery",
    card: "revision-bypass-gastrico",
    inNav: false,
  },
  revisionsBand: {
    es: "/es/cirugia-de-revision/banda-gastrica",
    en: "/services/revisions/lap-band",
    label: "Retiro de banda",
    title: "Retiro y Revisión de LAP-BAND en Virginia | Virginia Bariatric",
    description:
      "Retiro de banda gástrica y conversión a manga o bypass por deslizamiento, reflujo o poca pérdida de peso, en Herndon, Virginia, con el Dr. Matthew Fitzer.",
    ogTitle: "Retiro y Revisión de LAP-BAND en Virginia | Virginia Bariatric",
    ogDescription:
      "Retiro de banda gástrica y conversión a manga o bypass por deslizamiento, reflujo o poca pérdida de peso, en Herndon, Virginia, con el Dr. Matthew Fitzer.",
    ogImageAlt: "Retiro y conversión de banda gástrica en Virginia Bariatric Surgery",
    card: "revision-banda-gastrica",
    inNav: false,
  },
  revisionsTore: {
    es: "/es/cirugia-de-revision/tore",
    en: "/services/revisions/tore",
    label: "TORe",
    title: "TORe para perder Peso con el Bypass en Virginia | Virginia Bariatric",
    description:
      "TORe en el norte de Virginia: procedimiento endoscópico ambulatorio sin incisiones para la recuperación de peso tras el bypass gástrico, por el Dr. Fitzer.",
    ogTitle: "TORe para perder Peso con el Bypass en Virginia | Virginia Bariatric",
    ogDescription:
      "TORe en el norte de Virginia: procedimiento endoscópico ambulatorio sin incisiones para la recuperación de peso tras el bypass gástrico, por el Dr. Fitzer.",
    ogImageAlt: "Consultorio de cirugía bariátrica y pérdida de peso en el norte de Virginia — Dr. Matthew Fitzer, cirujano bariátrico certificado",
    card: "tore",
    inNav: false,
  },
  bypassVsSleeve: {
    es: "/es/bypass-o-manga-gastrica",
    en: "/services/bypass-vs-sleeve",
    label: "Bypass o manga",
    title: "Bypass Gástrico o Manga Gástrica: ¿Cuál Elegir? | Virginia Bariatric",
    description:
      "Bypass gástrico o manga gástrica: pérdida de peso, reflujo, diabetes, riesgos y recuperación, explicados por un cirujano bariátrico certificado.",
    ogTitle: "Bypass Gástrico o Manga Gástrica: ¿Cuál Elegir? | Virginia Bariatric",
    ogDescription:
      "Bypass gástrico o manga gástrica: pérdida de peso, reflujo, diabetes, riesgos y recuperación, explicados por un cirujano bariátrico certificado.",
    ogImageAlt:
      "Comparación entre bypass gástrico y manga gástrica en Virginia Bariatric Surgery",
    card: "bypass-o-manga",
    inNav: false,
  },
  faq: {
    es: "/es/preguntas-frecuentes",
    en: "/resources/faq",
    label: "Preguntas frecuentes",
    title: "Preguntas Frecuentes en Español | Virginia Bariatric",
    description:
      "Respuestas a las preguntas más comunes sobre cirugía bariátrica, manga gástrica, medicamentos GLP-1, seguro y recuperación en Virginia Bariatric Surgery.",
    ogTitle: "Preguntas Frecuentes en Español | Virginia Bariatric",
    ogDescription:
      "Respuestas a las preguntas más comunes sobre cirugía bariátrica, manga gástrica, medicamentos GLP-1, seguro y recuperación en Virginia Bariatric Surgery.",
    ogImageAlt:
      "Preguntas frecuentes sobre cirugía bariátrica en Virginia Bariatric Surgery",
    card: "preguntas-frecuentes",
  },
  qualifications: {
    es: "/es/requisitos-para-cirugia",
    en: "/resources/qualifications",
    label: "¿Califico para cirugía?",
    title: "¿Califico para la Cirugía Bariátrica? | Virginia Bariatric",
    description:
      "Requisitos de IMC para cirugía bariátrica: umbrales con seguro y de pago propio, condiciones que califican y ajustes por ascendencia del este de Asia.",
    ogTitle: "¿Califico para la Cirugía Bariátrica? | Virginia Bariatric",
    ogDescription:
      "Requisitos de IMC para cirugía bariátrica: umbrales con seguro y de pago propio, condiciones que califican y ajustes por ascendencia del este de Asia.",
    ogImageAlt:
      "Requisitos para la cirugía bariátrica: IMC, condiciones médicas y criterios del seguro en Virginia Bariatric Surgery",
    card: "requisitos-cirugia",
    inNav: false,
  },
  diabetes: {
    es: "/es/obesidad-y-diabetes",
    en: "/resources/obesity-and-diabetes",
    label: "Obesidad y diabetes",
    title: "Resistencia a la Insulina y Cirugía Bariátrica | Virginia Bariatric",
    description:
      "Obesidad y resistencia a la insulina: cómo se mide y qué muestran los estudios sobre cirugía bariátrica, medicamentos y remisión de la diabetes tipo 2.",
    ogTitle: "Resistencia a la Insulina y Cirugía Bariátrica | Virginia Bariatric",
    ogDescription:
      "Obesidad y resistencia a la insulina: cómo se mide y qué muestran los estudios sobre cirugía bariátrica, medicamentos y remisión de la diabetes tipo 2.",
    ogImageAlt:
      "Obesidad, resistencia a la insulina y cirugía bariátrica en Virginia Bariatric Surgery",
    card: "obesidad-y-diabetes",
    inNav: false,
  },
  selfPay: {
    es: "/es/cirugia-bariatrica-de-pago-directo",
    en: "/self-pay-bariatric-surgery",
    label: "Precios de pago propio",
    title: "Precios de Cirugía Bariátrica de Pago Propio | Virginia Bariatric",
    description:
      "Precios de pago propio para manga gástrica, bypass, Lap-Band y cirugía de revisión en el norte de Virginia. Qué incluye, formas de pago y próximos pasos.",
    ogTitle: "Precios de Cirugía Bariátrica de Pago Propio | Virginia Bariatric",
    ogDescription:
      "Precios de pago propio para manga gástrica, bypass, Lap-Band y cirugía de revisión en el norte de Virginia. Qué incluye, formas de pago y próximos pasos.",
    ogImageAlt:
      "Precios de pago propio para cirugía bariátrica en Virginia Bariatric Surgery",
    card: "cirugia-pago-propio",
    inNav: false,
  },
  blog: {
    es: "/es/el-doctor-analiza-las-noticias",
    en: "/dr-fitzer-dissects-the-news",
    label: "Noticias",
    title: "El Dr. Fitzer Analiza las Noticias | Virginia Bariatric",
    description:
      "El Dr. Matthew Fitzer analiza las últimas noticias sobre cirugía bariátrica, pérdida de peso y nutrición.",
    ogTitle: "El Dr. Fitzer Analiza las Noticias | Virginia Bariatric",
    ogDescription:
      "El Dr. Matthew Fitzer analiza las últimas noticias sobre cirugía bariátrica, pérdida de peso y nutrición.",
    ogImageAlt:
      "El Dr. Fitzer Analiza las Noticias — análisis de cirugía bariátrica del Dr. Matthew Fitzer",
    card: "noticias",
  },
  postPart1: {
    es: "/es/el-doctor-analiza-las-noticias/recuperacion-de-peso-tras-wegovy-zepbound-parte-1",
    en: "/dr-fitzer-dissects-the-news/weight-regain-after-wegovy-zepbound-treatment-part-1",
    label: "Recuperación de peso, parte 1",
    title: "Recuperación de peso tras Wegovy/Zepbound: Parte 1 | Dr. Fitzer",
    description:
      "Parte 1: por qué se espera recuperar peso al dejar Wegovy o Zepbound y por qué los estudios de reducción gradual se quedan cortos. Análisis del Dr. Fitzer.",
    ogTitle: "¿Recuperación de peso tras el tratamiento con Wegovy/Zepbound? — Parte 1",
    ogDescription:
      "Por qué se espera la recuperación de peso al suspender Wegovy o Zepbound, y por qué los estudios de reducción gradual se quedan cortos.",
    ogImageAlt:
      "Una sola pluma de inyección para bajar de peso sobre una superficie clínica blanca y brillante",
    card: "noticias-parte-1",
    inNav: false,
  },
  postPart2: {
    es: "/es/el-doctor-analiza-las-noticias/recuperacion-de-peso-tras-wegovy-zepbound-parte-2",
    en: "/dr-fitzer-dissects-the-news/weight-regain-after-wegovy-zepbound-treatment-part-2",
    label: "Recuperación de peso, parte 2",
    title: "Recuperación de peso tras Wegovy/Zepbound: Parte 2 | Dr. Fitzer",
    description:
      "Parte 2: ¿el estilo de vida, el ejercicio o cambiar de medicamento evitan recuperar peso tras dejar Wegovy o Zepbound? El Dr. Fitzer analiza la evidencia.",
    ogTitle: "¿Recuperación de peso tras el tratamiento con Wegovy/Zepbound? — Parte 2",
    ogDescription:
      "¿El estilo de vida, el ejercicio o cambiar de medicamento previenen la recuperación de peso? El Dr. Fitzer analiza la evidencia.",
    ogImageAlt:
      "Vista aérea de zapatos deportivos, un plato de vegetales frescos y una pluma de inyección para bajar de peso sobre una superficie blanca",
    card: "noticias-parte-2",
    inNav: false,
  },
  postPart3: {
    es: "/es/el-doctor-analiza-las-noticias/recuperacion-de-peso-tras-wegovy-zepbound-parte-3",
    en: "/dr-fitzer-dissects-the-news/weight-regain-after-wegovy-zepbound-treatment-part-3",
    label: "Recuperación de peso, parte 3",
    title: "Recuperación de peso tras Wegovy/Zepbound: Parte 3 | Dr. Fitzer",
    description:
      "Parte 3: ¿Puede la cirugía bariátrica evitar recuperar peso tras dejar Wegovy o Zepbound? El Dr. Matthew Fitzer explica las opciones a largo plazo.",
    ogTitle: "¿Recuperación de peso después del tratamiento con Wegovy/Zepbound? — Parte 3",
    ogDescription:
      "¿Puede la cirugía bariátrica prevenir la recuperación de peso tras dejar Wegovy o Zepbound? Las opciones a largo plazo.",
    ogImageAlt: "Un quirófano vacío e iluminado con equipo laparoscópico y luces quirúrgicas",
    card: "noticias-parte-3",
    inNav: false,
  },
  postRegainReality: {
    es: "/es/el-doctor-analiza-las-noticias/recuperacion-de-peso-tras-dejar-inyectables-la-realidad",
    en: "/dr-fitzer-dissects-the-news/weight-regain-after-stopping-injectable-medication-whats-the-reality",
    label: "La realidad de la recuperación de peso",
    title: "¿Recuperaré peso al dejar Wegovy/Zepbound? | Dr. Fitzer",
    description:
      "La verdad simple: en ausencia de otra intervención médica o quirúrgica, quienes dejan los inyectables pueden esperar recuperar el 100% de lo que perdieron.",
    ogTitle: "¿Recuperaré peso al dejar Wegovy/Zepbound?",
    ogDescription:
      "Qué muestran realmente los estudios sobre la recuperación de peso tras suspender los inyectables, explicado por el Dr. Fitzer.",
    ogImageAlt: "Una báscula de baño vacía sobre un piso de madera a la luz de la mañana",
    card: "noticias-recuperacion-realidad",
    inNav: false,
  },
  postSemaglutideSurgery: {
    es: "/es/el-doctor-analiza-las-noticias/semaglutida-contra-cirugia-resultados",
    en: "/dr-fitzer-dissects-the-news/semaglutide-versus-weight-loss-surgery-outcomes",
    label: "Wegovy contra la cirugía",
    title: "Wegovy contra los resultados de la cirugía | Dr. Fitzer",
    description:
      "¿Funciona Wegovy en dosis máxima tan bien como la cirugía? No. Los pacientes de manga y bypass del Dr. Fitzer pierden de 2.6 a 3 veces más peso a los dos años.",
    ogTitle: "Wegovy contra los resultados de la cirugía",
    ogDescription:
      "Wegovy en dosis máxima frente a la manga gástrica y el bypass: los resultados reales a dos años.",
    ogImageAlt:
      "Una pluma inyectora para bajar de peso enfocada sobre una superficie gris, con un quirófano de hospital desenfocado detrás",
    card: "noticias-semaglutida-cirugia",
    inNav: false,
  },
  postTenYear: {
    es: "/es/el-doctor-analiza-las-noticias/nuestros-datos-de-perdida-de-peso-a-diez-anos",
    en: "/dr-fitzer-dissects-the-news/our-ten-year-weight-loss-data",
    label: "Datos a diez años",
    title: "¡Nuestros datos de pérdida de peso a diez años! | Dr. Fitzer",
    description:
      "Datos reales de pérdida de peso a diez años del consultorio del Dr. Fitzer: resultados del bypass gástrico y la gastrectomía en manga, y por qué importa la durabilidad.",
    ogTitle: "¡Nuestros datos de pérdida de peso a diez años!",
    ogDescription:
      "Resultados reales a diez años del bypass gástrico y la manga gástrica en el consultorio del Dr. Fitzer.",
    ogImageAlt:
      "Datos de pérdida de peso a diez años de Virginia Bariatric Surgery",
    card: "noticias-diez-anos",
    inNav: false,
  },
  postGlp1Stat: {
    es: "/es/el-doctor-analiza-las-noticias/manejo-de-glp-1-al-instante",
    en: "/dr-fitzer-dissects-the-news/glp-1-management-stat",
    label: "GLP-1 al instante",
    title: "¡Manejo de GLP-1 al instante! | Dr. Fitzer",
    description:
      "Una cadena de urgencias ofrece manejo por telesalud de medicamentos GLP-1 para bajar de peso. El Dr. Matthew Fitzer opina sobre quién debería manejarlos.",
    ogTitle: "¡Manejo de GLP-1 al instante!",
    ogDescription:
      "¿Debería un centro de atención de urgencias manejar sus medicamentos GLP-1? Opina el Dr. Fitzer.",
    ogImageAlt:
      "Teléfono inteligente sobre un escritorio de clínica mostrando una consulta de video de telesalud con un profesional clínico",
    card: "noticias-glp-1-stat",
    inNav: false,
  },
  postEyeProblems: {
    es: "/es/el-doctor-analiza-las-noticias/problemas-de-la-vista-y-medicamentos-glp-1",
    en: "/dr-fitzer-dissects-the-news/eye-problems-and-glp-1-medications",
    label: "La vista y los GLP-1",
    title: "Problemas de vista y GLP-1 | Dr. Fitzer",
    description:
      "¿Puede Wegovy o Zepbound dañar sus ojos? El Dr. Matthew Fitzer explica la retinopatía diabética, la NAION y cómo evalúa estos riesgos.",
    ogTitle: "Problemas de vista y GLP-1",
    ogDescription:
      "Retinopatía diabética, NAION y cómo evaluamos a los pacientes que usan semaglutida o tirzepatida.",
    ogImageAlt:
      "Primer plano extremo de un ojo humano que muestra el iris y la pupila, ilustrando los riesgos para la vista discutidos con los medicamentos GLP-1",
    card: "noticias-vista-glp-1",
    inNav: false,
  },
  postAlcoholism: {
    es: "/es/el-doctor-analiza-las-noticias/riesgo-de-alcoholismo-en-pacientes-de-bypass-gastrico",
    en: "/dr-fitzer-dissects-the-news/why-are-gastric-bypass-patients-at-risk-for-alcoholism",
    label: "Bypass y alcoholismo",
    title: "Bypass gástrico y riesgo de alcoholismo | Dr. Fitzer",
    description:
      "El bypass gástrico eleva el riesgo de alcoholismo. El Dr. Fitzer explica su vínculo con la hipoglucemia, los síntomas y la higiene de carbohidratos.",
    ogTitle: "¿Por qué los pacientes de bypass gástrico están en riesgo de alcoholismo?",
    ogDescription:
      "El vínculo entre el bypass gástrico, la hipoglucemia y el riesgo de alcoholismo, explicado por el Dr. Fitzer.",
    ogImageAlt:
      "El riesgo de alcoholismo después de la cirugía de bypass gástrico, explicado por el Dr. Fitzer",
    card: "noticias-alcoholismo-bypass",
    inNav: false,
  },
  postBreastCancer: {
    es: "/es/el-doctor-analiza-las-noticias/los-glp-1-mejoran-la-supervivencia-en-cancer-de-mama",
    en: "/dr-fitzer-dissects-the-news/glp-1s-improve-survival-for-women-with-breast-cancer",
    label: "GLP-1 y cáncer de mama",
    title: "Los GLP-1 y la supervivencia en cáncer de mama | Dr. Fitzer",
    description:
      "Los GLP-1 muestran gran beneficio de supervivencia en cáncer de mama, hasta 63% menos mortalidad. El Dr. Fitzer revisa los datos y los motivos de cautela.",
    ogTitle: "Los GLP-1 mejoran la supervivencia en cáncer de mama",
    ogDescription:
      "Qué muestran realmente los estudios de GLP-1 en cáncer de mama, y por qué el beneficio probablemente sea menor de lo reportado.",
    ogImageAlt:
      "Gráfico de bosque que compara la cirugía bariátrica y los agonistas del receptor de GLP-1 en desenlaces del cáncer de mama",
    card: "noticias-cancer-de-mama",
    inNav: false,
  },
  postMagdi: {
    es: "/es/el-doctor-analiza-las-noticias/cirugia-magnetica-para-bajar-de-peso-guia-de-magdi",
    en: "/dr-fitzer-dissects-the-news/magnetic-weight-loss-surgery-a-primer-on-magdi",
    label: "Cirugía magnética y MagDI",
    title: "Cirugía magnética para bajar de peso: MagDI | Virginia Bariatric",
    description:
      "El Dr. Fitzer analiza MagDI y MagGI: cómo funcionan los imanes, los resultados publicados, las complicaciones y la autorización de la FDA.",
    ogTitle: "¿5 % o 34 %? Lo que logra de verdad la cirugía magnética para bajar de peso",
    ogDescription:
      "El Dr. Fitzer revisa todos los estudios publicados sobre MagDI: cuánto peso se perdió con y sin manga, las complicaciones y lo que la FDA realmente autorizó.",
    ogImageAlt: "Cirugía con imanes: ¿5 % o 34 %? — Virginia Bariatric",
    card: "noticias-magdi-v2",
    inNav: false,
  },
} as const satisfies Record<string, EsPage>;

export type EsPageKey = keyof typeof ES_PAGES;

/** Every Spanish page, including deep pages kept out of the section nav. */
export const ES_ALL_PAGES: EsPage[] = Object.values(ES_PAGES);

export const ES_NAV: EsPage[] = ES_ALL_PAGES.filter((p) => p.inNav !== false);

/**
 * Spanish-only navigation shown on /es routes. Deep pages stay `inNav: false`
 * for the English chrome, but they are grouped here so Spanish visitors (and
 * crawlers on /es pages) can reach every Spanish page from one menu.
 */
export interface EsNavGroup {
  label: string;
  /** Pages listed under the group; the first one is the section hub. */
  items: EsPageKey[];
}

export const ES_NAV_GROUPS = [
  {
    label: "Cirugía",
    items: [
      "services",
      "sleeve",
      "bypass",
      "bypassVsSleeve",
      "balloon",
      "esg",
      "band",
      "revisions",
      "revisionsSleeve",
      "revisionsBypass",
      "revisionsBand",
      "hiatalHernia",
      "qualifications",
    ],
  },
  {
    label: "Medicamentos",
    items: ["medical", "medications", "semaglutide", "tirzepatide", "oralMedications", "medQualifications"],
  },
  {
    label: "Seguro",
    items: [
      "insurance",
      "insuranceCommercial",
      "insuranceMedicaid",
      "insuranceMedicare",
      "insuranceCashPay",
      "insuranceNc",
      "surgeryInsurance",
      "financing",
      "selfPay",
    ],
  },
] as const satisfies readonly EsNavGroup[];

/** Standalone links that sit beside the grouped menus in the Spanish nav. */
export const ES_NAV_LINKS = ["home", "about", "stories", "local", "blog", "faq", "contact"] as const satisfies readonly EsPageKey[];

/**
 * Keys that appear in the Spanish nav — only static routes, so EsNav can pass
 * each page's path straight to <Link to>. Dynamic blog-article keys stay out.
 */
export type EsNavKey =
  | (typeof ES_NAV_LINKS)[number]
  | (typeof ES_NAV_GROUPS)[number]["items"][number];

/**
 * Spanish footer columns. Every key in ES_PAGES appears here exactly once, so
 * each Spanish page gets a crawlable link from every other Spanish page and
 * nothing links out to an English-only URL. Adding a Spanish page to ES_PAGES
 * means adding it here too; scripts/es-footer-check.mjs enforces that.
 */
export const ES_FOOTER_GROUPS = [
  {
    label: "Cirugía bariátrica",
    items: [
      "services",
      "sleeve",
      "bypass",
      "bypassVsSleeve",
      "balloon",
      "esg",
      "band",
      "revisions",
      "revisionsSleeve",
      "revisionsBypass",
      "revisionsBand",
      "revisionsTore",
      "hiatalHernia",
      "qualifications",
      "diabetes",
      "weightRegainGuide",
      "selfPay",
    ],
  },
  {
    label: "Medicamentos y seguro",
    items: [
      "medical",
      "medications",
      "semaglutide",
      "tirzepatide",
      "oralMedications",
      "medQualifications",
      "insurance",
      "insuranceCommercial",
      "insuranceMedicaid",
      "insuranceMedicare",
      "insuranceCashPay",
      "insuranceNc",
      "surgeryInsurance",
      "financing",
    ],
  },
  {
    label: "Explorar",
    items: [
      "home",
      "about",
      "stories",
      "local",
      "faq",
      "contact",
      "blog",
      "postPart1",
      "postPart2",
      "postPart3",
      "postRegainReality",
      "postSemaglutideSurgery",
      "postTenYear",
      "postGlp1Stat",
      "postEyeProblems",
      "postAlcoholism",
      "postBreastCancer",
      "postMagdi",
    ],
  },
] as const satisfies readonly { label: string; items: readonly EsPageKey[] }[];

/** URL prefix shared by every Spanish blog article. */
export const ES_BLOG_PREFIX = "/es/el-doctor-analiza-las-noticias/";

/** ES_PAGES key for a Spanish blog article slug; falls back to the blog index. */
export function esPostKeyForSlug(slug: string): EsPageKey {
  const path = `${ES_BLOG_PREFIX}${slug}`;
  for (const [key, page] of Object.entries(ES_PAGES)) {
    if ((page as EsPage).es === path) return key as EsPageKey;
  }
  return "blog";
}


/** hreflang alternates for a Spanish/English page pair. */
export function hreflangLinks(page: EsPage) {
  if (page.noAlternate) {
    return [
      { rel: "alternate", hrefLang: "es", href: `${SITE}${page.es}` },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE}${page.es}` },
    ];
  }
  return [
    { rel: "alternate", hrefLang: "es", href: `${SITE}${page.es}` },
    { rel: "alternate", hrefLang: "en", href: `${SITE}${page.en}` },
    { rel: "alternate", hrefLang: "x-default", href: `${SITE}${page.en}` },
  ];
}

/** Complete head() payload for a Spanish landing page. */
/** Absolute URL of the Spanish share card for a page. */
export function esCardUrl(key: EsPageKey) {
  return `${SITE}/og/es/og-${ES_PAGES[key].card}.png`;
}

export function esHead(key: EsPageKey) {
  const page = ES_PAGES[key];
  const url = `${SITE}${page.es}`;
  const image = key === "revisionsTore" ? OG_IMAGE : esCardUrl(key);

  return {
    meta: [
      { title: page.title },
      { name: "description", content: page.description },
      { property: "og:title", content: page.ogTitle },
      { property: "og:description", content: page.ogDescription },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "es_LA" },
      { property: "og:locale:alternate", content: "en_US" },
      { property: "og:image", content: image },
      { property: "og:image:alt", content: page.ogImageAlt },
      { property: "og:image:type", content: key === "revisionsTore" ? "image/jpeg" : "image/png" },
      { property: "og:image:width", content: key === "revisionsTore" ? "1200" : "2400" },
      { property: "og:image:height", content: key === "revisionsTore" ? "630" : "1260" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: page.ogTitle },
      { name: "twitter:description", content: page.ogDescription },
      { name: "twitter:image", content: image },
      { name: "twitter:image:alt", content: page.ogImageAlt },
    ],
    links: [{ rel: "canonical", href: url }, ...hreflangLinks(page)],
  };
}

/**
 * English path -> Spanish path, for pages that declare an English twin.
 * Lets the English routes emit the reciprocal `workTranslation` schema link
 * from the same source of truth that drives hreflang.
 */
export const ES_BY_EN: Record<string, string> = Object.fromEntries(
  (Object.values(ES_PAGES) as EsPage[])
    .filter((page) => !page.noAlternate && page.en)
    .map((page) => [page.en, page.es]),
);

/** JSON-LD WebPage node in Spanish for a given page. */
/**
 * Spanish procedure pages and the fragment of their own MedicalProcedure node.
 * Mirrors the English PROCEDURE_PAGES rule: the WebPage is about the page's
 * procedure and mentions the sitewide Hospital node by @id.
 */
const ES_PROCEDURE_FRAGMENT: Partial<Record<EsPageKey, string>> = {
  sleeve: "procedimiento",
  bypass: "procedimiento",
  esg: "procedimiento",
  balloon: "procedimiento",
  band: "procedimiento",
  hiatalHernia: "procedimiento",
  revisionsSleeve: "procedure",
  revisionsBypass: "procedure",
  revisionsBand: "procedure",
  revisionsTore: "procedure",
};

export function esWebPageSchema(key: EsPageKey) {
  const page: EsPage = ES_PAGES[key];
  const url = `${SITE}${page.es}`;
  const procedureFragment = ES_PROCEDURE_FRAGMENT[key];
  // Tie the Spanish node to the English original so crawlers read the pair as
  // one work in two languages rather than two competing pages.
  const translationOfWork = page.noAlternate
    ? undefined
    : { "@id": `${SITE}${page.en}#webpage` };
  // Keep this reference self-contained. Some audit crawlers validate each
  // JSON-LD block independently and otherwise treat a bare organization @id
  // as an incomplete LocalBusiness, even when the full node is in root JSON-LD.
  const provider = {
    "@type": "MedicalClinic",
    "@id": `${SITE}/#organization`,
    name: "Virginia Bariatric Surgery",
    url: `${SITE}/`,
    telephone: "+1-703-709-9771",
    address: {
      "@type": "PostalAddress",
      streetAddress: "171 Elden Street, Suite 3B",
      addressLocality: "Herndon",
      addressRegion: "VA",
      postalCode: "20170",
      addressCountry: "US",
    },
  };
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: page.title,
    description: page.description,
    inLanguage: "es-US",
    isPartOf: { "@id": `${SITE}/#website` },
    about: provider,
    provider,
    // Review date = edit date: the same page content date the visible
    // "Última revisión y edición:" line and the sitemap <lastmod> publish.
    ...schemaDateFields(page.es),
    ...(translationOfWork ? { translationOfWork } : {}),
    ...(procedureFragment
      ? {
          about: { "@id": `${url}#${procedureFragment}` },
          mainEntity: { "@id": `${url}#${procedureFragment}` },
          mentions: [{ "@id": `${SITE}/#inova-fair-oaks` }],
        }
      : {}),
  };
}
